import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';

interface BillingSummary {
  subscriptionStatus: string;
  planLabel: string;
}

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 30_000;

export function BillingSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const startedAt = useRef<number>(Date.now());
  const syncAttempted = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const trySync = async () => {
      if (syncAttempted.current) return;
      syncAttempted.current = true;
      const sessionId = searchParams.get('session_id');
      if (!sessionId) return;
      try {
        await api.post('/billing/sync', { sessionId });
      } catch {
        /* best-effort; polling will pick up state regardless */
      }
    };

    const tick = async () => {
      await trySync();
      try {
        const res = await api.get<BillingSummary>('/billing/summary');
        if (cancelled) return;
        setSummary(res.data);
        if (
          res.data.subscriptionStatus === 'active' ||
          res.data.subscriptionStatus === 'trialing'
        ) {
          setTimeout(() => {
            if (!cancelled) navigate('/billing');
          }, 1200);
          return;
        }
      } catch {
        /* ignore — keep polling */
      }
      if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
        if (!cancelled) setTimedOut(true);
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [navigate, searchParams]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6">
      <div className="max-w-lg w-full border border-green-200 bg-green-50 rounded-lg p-6 space-y-3 text-center">
        <h1 className="text-xl font-bold text-green-900">Payment received</h1>
        {summary?.subscriptionStatus === 'active' || summary?.subscriptionStatus === 'trialing' ? (
          <p className="text-sm text-green-800">
            Your {summary.planLabel} plan is now active. Redirecting you to billing…
          </p>
        ) : timedOut ? (
          <p className="text-sm text-green-800">
            We're still syncing your subscription. Refresh the billing page in a moment.
          </p>
        ) : (
          <p className="text-sm text-green-800">Finalizing your subscription…</p>
        )}
        <Link
          to="/billing"
          className="inline-block px-4 py-2 text-sm font-semibold text-green-900 bg-white border border-green-300 rounded hover:bg-green-100"
        >
          Go to billing
        </Link>
      </div>
    </div>
  );
}

export default BillingSuccessPage;
